<template lang="pug">
  yz-dialog(v-model="showDialog" max-width="1200px" persistent scrollable)
    yz-card
      // Header
      yz-dialog-normal-header(title="Documents" divider-color="yz-neutral-400" @close="onCloseDialog")

      // Main Content
      yz-dialog-normal-content.pa-0
        v-row(no-gutters style="height: 650px;")
          // Left Panel - Documents
          v-col(cols="8")
            // Panel Info Section
            .pa-3.bg-neutral-300
              v-row(no-gutters, align="center")
                v-col(cols="5")
                  .d-flex.align-center.gap-2
                    .yz-heading-8 {{ parentClaim.panel.name }}
                    yz-badge(size="sm" color="#848484" text-color="white") Panel
                v-col
                  .yz-medium-body-xs.text-color-hint.mb-1 Approved amount
                  .yz-medium-body-md.text-success {{ parentClaim.approved_amount | displayPrice($currency) }}
                v-col
                  .yz-medium-body-xs.text-color-hint.mb-1 Approved date
                  .yz-medium-body-md
                    span(v-if="parentClaim.approved_date") {{ formatDate(parentClaim.approved_date) }}
                    span.text-neutral-500(v-else) N/A
                v-col
                  .yz-medium-body-xs.text-color-hint.mb-1 Remarks
                  .yz-medium-body-md
                    span(v-if="parentClaim.remarks") {{ parentClaim.remarks }}
                    span.text-neutral-500(v-else) N/A

            yz-divider(color="yz-neutral-400")

            // Action Buttons
            .action-buttons.pa-3.d-flex.align-center.justify-space-between
              yz-btn(:label="viewMode === 'thumbnail' ? 'Switch to list' : 'Switch to thumbnail'" size="lg" outlined :append-icon="viewMode === 'thumbnail' ? '$yzi-list' : '$yzi-grid-01'" background-color="white" @click="onSwitchToList")
              .d-flex.gap-2
                yz-btn(label="Upload new" size="lg" color="#333333" text-color="white" @click="onUploadNew")

            yz-divider

            // Documents Grid
            .documents-grid.pa-4(v-if="viewMode === 'thumbnail'")
              v-row
                v-col(v-for="document in documentsData" :key="document.id" cols="4" @click="onDocumentSelect(document)")
                  .document-thumbnail-item
                    .thumbnail-wrapper.position-relative
                      // Checkbox
                      .checkbox-overlay.position-absolute
                        yz-checkbox(:value="document.selected" @change="onDocumentSelect(document)" @click.stop)

                      // Thumbnail
                      // Image thumbnail
                      yz-img.document-thumbnail(v-if="document.fileCategory === 'Image' && document.file" :src="document.file" width="100" height="100" cover)
                      // PDF/Others thumbnail
                      .document-thumbnail.d-flex.align-center.justify-center(v-else :style="{ backgroundImage: document.thumbnail ? `url(${document.thumbnail})` : 'none' }" :class="{ 'loading': document.thumbnailLoading }")
                        yz-icon(v-if="!document.thumbnail && !document.thumbnailLoading" size="24px" color="#C1C1C1") {{ getDocumentIcon(document.fileCategory) }}
                        v-progress-circular(v-if="document.thumbnailLoading" indeterminate size="20" width="2" color="primary")

                      // Document Name
                      .yz-regular-body-xs.text-center.mt-1 {{ document.filename }}

            // Documents Table
            .documents-table.pa-4(v-else)
              yz-data-table(:headers="tableHeaders" :items="documentsData" item-key="hid" show-select, @click:row="onDocumentSelect")
                template(v-slot:header.data-table-select)
                  yz-checkbox(:indeterminate="someDocumentsSelected" :value="allDocumentsSelected" @change="onSelectAll")

                template(v-slot:item.data-table-select="{ item }")
                  yz-checkbox(:value="item.selected" @change="onDocumentSelect(item)" @click.stop)

                template(v-slot:item.filename="{ item }")
                  .yz-regular-body-sm {{ item.filename }}

                template(v-slot:item.type="{ item }")
                  .yz-regular-body-sm {{ getFileExtension(item.type) }}

                template(v-slot:item.created="{ item }")
                  .yz-regular-body-sm {{ formatDate(item.created) }}

                template(v-slot:item.uploaded_by="{ item }")
                  yz-badge(v-if="item.uploaded_by" border size="sm")
                    span {{ getUploaderName(item.uploaded_by) }}
                  span.text-neutral-500(v-else) N/A

          yz-divider.ma-0(vertical, color="yz-neutral-400")

          // Right Panel - Preview
          v-col
            .d-flex.align-center.justify-center.fill-height
              // No Selection State
              .preview-container(v-if="previewMode === 'none'")
                .text-center.align-center.d-flex.flex-column.justify-center.fill-height
                  .yz-medium-body-sm.mb-1 No document selected
                  .yz-regular-body-sm.text-color-hint Select files on the left pane to view details

              // Single Selection State
              .preview-container.single-selection(v-else-if="previewMode === 'single'")
                // Large Preview
                .preview-image.mb-4
                  // PDF Preview
                  .pdf-preview(v-if="selectedDocuments[0].fileCategory === 'PDF'")
                    .pdf-viewer-container
                      canvas(ref="pdfCanvas" style="max-width: 100%; height: 132px; border: 1px solid #E5E5E5;")
                      .d-flex.align-center.justify-center.mt-3(v-if="selectedDocuments[0].pdfLoading")
                        v-progress-circular(indeterminate size="24" width="3" color="primary")
                        span.ml-2.yz-regular-body-sm Loading PDF...

                  // Image Preview
                  .image-preview.d-flex.justify-center(v-else-if="selectedDocuments[0].fileCategory === 'Image'")
                    yz-img(:src="selectedDocuments[0].file" width="300px" alt="Document preview")

                  // Others Preview
                  .others-preview(v-else-if="selectedDocuments[0].fileCategory === 'Others'")
                    .d-flex.align-center.justify-center(style="height: 132px; border: 1px solid #E5E5E5; background-color: #F5F5F5; border-radius: 4px;")
                      .text-center
                        yz-icon(size="48px" color="#C1C1C1") $yzi-file-02
                        .yz-regular-body-sm.text-color-hint.mt-2 Preview not available

                // Document Name
                .yz-heading-8.mb-4 {{ selectedDocuments[0].filename }}

                // Action Buttons
                .d-flex.justify-center.gap-2.my-4
                  yz-btn(label="Delete" color="danger" size="sm" outlined, @click="onDeleteDocument")
                  yz-btn(label="Download" size="sm" outlined @click="onDownloadFile")

                yz-divider.my-4

                // File Details
                .file-details
                  .yz-heading-8.mb-3 File details
                  .details-grid
                    .detail-item.mb-3
                      .yz-regular-body-xs.text-color-hint File type
                      .yz-medium-body-sm {{ getFileExtension(selectedDocuments[0].type) }}
                    .detail-item.mb-3
                      .yz-regular-body-xs.text-color-hint Size
                      .yz-medium-body-sm {{ formatFileSize(selectedDocuments[0].size) }}
                    .detail-item.mb-3
                      .yz-regular-body-xs.text-color-hint Created
                      .yz-medium-body-sm {{ formatDate(selectedDocuments[0].created) }}
                    .detail-item(v-if="selectedDocuments[0].uploaded_by")
                      .yz-regular-body-xs.text-color-hint Uploaded by
                      .yz-medium-body-sm.mt-1 {{ getUploaderName(selectedDocuments[0].uploaded_by) }}

              // Multiple Selection State
              .preview-container.multiple-selection(v-else-if="previewMode === 'multiple'")
                // Header with actions
                .selection-header.d-flex.align-center.justify-space-between.px-4.pt-4
                  .yz-heading-10.text-neutral-700 Selected
                  .action-icons.d-flex.gap-2
                    yz-btn(label="Unselect all" size="sm" outlined @click="onUnselectAll")
                    yz-btn(icon="$yzi-trash-03" size="sm" outlined, color="danger" @click="onDeleteDocument")

                // Selected Documents List
                .selected-documents-list.px-4
                  .selected-document-item.mb-3(v-for="document in selectedDocuments" :key="document.hid")
                    v-row(no-gutters, align="center")
                      v-col
                        // Thumbnail
                        .document-thumbnail-small.mr-3
                          // Image thumbnail
                          yz-img.thumbnail-container(v-if="document.fileCategory === 'Image' && document.file" :src="document.file" width="70" height="70" cover)
                          // PDF/Others thumbnail
                          .thumbnail-container.d-flex.align-center.justify-center(
                            v-else
                            :style="{ backgroundImage: document.thumbnail ? `url(${document.thumbnail})` : 'none' }"
                          )
                            yz-icon(v-if="!document.thumbnail" size="20px" color="#C1C1C1") {{ getDocumentIcon(document.fileCategory) }}

                      v-col(cols="8")
                        // Document Name
                        .yz-regular-body-sm {{ formatFilename(document.filename, 20) }}

                      v-col
                        .d-flex.justify-end
                          // Remove Button
                          yz-btn(icon-only icon="mdi-close" size="sm" @click="onRemoveFromSelection(document)")

      // Footer Actions
      yz-dialog-normal-actions(divider-color="yz-neutral-400")
        .d-flex.justify-end.gap-2
          yz-btn(label="Close" size="lg" @click="onCloseDialog")

    // Delete Document Confirmation Dialog
    yz-delete-confirmation-dialog(v-if="showDeleteDocumentDialog" v-model="showDeleteDocumentDialog" item="document" :file-data="selectedDocuments" :loading="isDeletingDocument" @confirmed="onConfirmDeleteDocument" @canceled="onCancelDeleteDocument")

    // Add Document Dialog
    add-document-dialog(v-if="showAddDocumentDialog" v-model="showAddDocumentDialog" :parent-claim="parentClaim" @document-added="onDocumentAdded")
</template>

<script>
import { generatePdfThumbnail, loadPdfPreview, formatFilename } from '@/utils'
import { DateTime } from 'luxon'
import { mapActions } from 'vuex'
import AddDocumentDialog from '@/components/clinic/panel/dialogs/AddDocumentDialog.vue'

export default {
  components: { AddDocumentDialog },
  props: {
    value: {
      type: Boolean,
      default: false
    },
    parentClaim: {
      type: Object,
      required: true
    }
  },
  data() {
    return {
      selectedDocumentHids: [], // Track selected document hids
      showDeleteDocumentDialog: false,
      showAddDocumentDialog: false,
      viewMode: 'thumbnail', // 'thumbnail' or 'table'
      tableHeaders: [
        { text: 'Name', value: 'filename' },
        { text: 'File type', value: 'type' },
        { text: 'Upload date', value: 'created' },
        { text: 'Uploaded by', value: 'uploaded_by' }
      ],
      isDeletingDocument: false
    }
  },
  computed: {
    documentsData() {
      // Transform parent claim documents to match the expected structure
      const documents = this.parentClaim?.documents || []
      return documents.map(doc => {
        // Determine document file category (preserve original doc.type from API)
        const isImage = doc.type?.startsWith('image/') || ['JPG', 'JPEG', 'PNG', 'GIF'].includes(doc.type?.toUpperCase())
        const isPdf = doc.type?.includes('pdf') || doc.type?.toUpperCase() === 'PDF'

        let fileCategory = 'Others'
        if (isImage) {
          fileCategory = 'Image'
        } else if (isPdf) {
          fileCategory = 'PDF'
        }

        return {
          ...doc,
          fileCategory,
          selected: this.selectedDocumentHids.includes(doc.hid),
          thumbnail: null,
          thumbnailLoading: false,
          pdfLoading: false
        }
      })
    },
    showDialog: {
      get() {
        return this.value
      },
      set(value) {
        this.$emit('input', value)
      }
    },
    selectedDocuments() {
      return this.documentsData.filter(doc => doc.selected)
    },
    selectedCount() {
      return this.selectedDocuments.length
    },
    previewMode() {
      if (this.selectedCount === 0) return 'none'
      if (this.selectedCount === 1) return 'single'
      return 'multiple'
    },
    allDocumentsSelected() {
      return this.documentsData.length > 0 && this.selectedDocuments.length === this.documentsData.length
    },
    someDocumentsSelected() {
      const hasSelected = this.selectedDocuments.length > 0
      return hasSelected && !this.allDocumentsSelected
    }
  },
  mounted() {
    this.generateThumbnails()
  },
  watch: {
    selectedDocuments: {
      handler(docs) {
        if (docs.length === 1 && docs[0].fileCategory === 'PDF') {
          this.$nextTick(() => {
            loadPdfPreview(docs[0], this.$refs.pdfCanvas, 550)
          })
        }
      },
      immediate: true
    }
  },
  methods: {
    ...mapActions(['showMessage']),
    onCloseDialog() {
      this.showDialog = false
    },
    onDocumentSelect(document) {
      // Toggle checkbox selection when thumbnail is clicked
      const index = this.selectedDocumentHids.indexOf(document.hid)
      if (index > -1) {
        this.selectedDocumentHids.splice(index, 1)
      } else {
        this.selectedDocumentHids.push(document.hid)
      }
    },
    onUploadNew() {
      this.showAddDocumentDialog = true
    },
    onSwitchToList() {
      this.viewMode = this.viewMode === 'thumbnail' ? 'table' : 'thumbnail'
    },
    generateThumbnails() {
      for (const document of this.documentsData) {
        if (document.fileCategory === 'PDF' && !document.thumbnail && !document.thumbnailLoading) {
          generatePdfThumbnail(document)
        } else if (document.fileCategory === 'Image' && !document.thumbnail) {
          document.thumbnail = document.file
        }
      }
    },
    onRemoveFromSelection(document) {
      const index = this.selectedDocumentHids.indexOf(document.hid)
      if (index > -1) {
        this.selectedDocumentHids.splice(index, 1)
      }
    },
    onUnselectAll() {
      this.selectedDocumentHids = []
    },
    getDocumentIcon(fileCategory) {
      // Return appropriate icon based on document file category
      if (fileCategory === 'PDF') {
        return '$file-text'
      } else if (fileCategory === 'Image') {
        return '$image'
      } else {
        return '$yzi-file-02'
      }
    },
    formatFilename(filename, length) {
      return formatFilename(filename, length)
    },
    onDeleteDocument() {
      this.showDeleteDocumentDialog = true
    },
    async onConfirmDeleteDocument() {
      if (this.selectedDocumentHids.length === 0) return

      this.isDeletingDocument = true
      try {
        // Call bulk delete endpoint with all selected document HIDs
        await this.$axios.post('/documents/bulk_delete', {
          documents: this.selectedDocumentHids
        })

        const count = this.selectedDocumentHids.length
        const message = count === 1 ? 'Document deleted successfully' : `${count} documents deleted successfully`
        await this.showMessage(['success', message])

        // Emit event to parent to refresh documents data
        this.$emit('updated', this.selectedDocumentHids)

        // Clear selection
        this.selectedDocumentHids = []
        this.showDeleteDocumentDialog = false
      } catch (error) {
        console.error('Error deleting document:', error)
        await this.showMessage(['error', 'Failed to delete document. Please try again.'])
      } finally {
        this.isDeletingDocument = false
      }
    },
    onCancelDeleteDocument() {
      this.showDeleteDocumentDialog = false
    },
    onDocumentAdded() {
      // Emit event to parent to refresh claim data
      this.$emit('updated')
    },
    onDownloadFile() {
      if (this.selectedDocuments.length === 1 && this.selectedDocuments[0].file) {
        window.open(this.selectedDocuments[0].file, '_blank')
      }
    },
    getFileExtension(mimeType) {
      if (!mimeType) return '-'
      return mimeType
        .split('/')
        .pop()
        .toUpperCase()
    },
    formatFileSize(bytes) {
      if (!bytes || bytes === 0) return '0 KB'
      const kb = bytes / 1024
      if (kb < 1024) {
        return `${Math.round(kb)} KB`
      }
      const mb = kb / 1024
      return `${mb.toFixed(1)} MB`
    },
    formatDate(isoString) {
      if (!isoString) return '-'
      const date = DateTime.fromISO(isoString)
      return date.isValid ? date.toFormat('dd MMM yyyy') : '-'
    },
    getUploaderName(uploadedBy) {
      if (!uploadedBy) return null
      const firstName = uploadedBy.first_name || ''
      const lastName = uploadedBy.last_name || ''
      return `${firstName} ${lastName}`.trim() || null
    },
    onSelectAll() {
      if (this.allDocumentsSelected) {
        this.selectedDocumentHids = []
      } else {
        this.selectedDocumentHids = this.documentsData.map(doc => doc.hid)
      }
    }
  }
}
</script>

<style scoped>
.documents-dialog {
  border-radius: 10px;
  box-shadow: 0px 4px 10.2px 0px rgba(0, 0, 0, 0.16);
  overflow: hidden;
}

.left-panel {
  background-color: white;
  height: 100%;
}

.panel-info {
  background-color: #eeeeee;
  border-bottom: 0.5px solid #c1c1c1;
}

.panel-header {
  align-items: center;
}

.panel-details {
  align-items: flex-start;
}

.detail-item {
  min-width: 0;
}

.action-buttons {
  background-color: #f9fafb;
  border-bottom: 0.5px solid #c1c1c1;
}

.documents-grid {
  background-color: white;
  overflow-y: auto;
  max-height: 519px;
}

.document-thumbnail-item {
  cursor: pointer;
  padding: 10px;
  border-radius: 10px;
  position: relative;
}

.thumbnail-wrapper {
  position: relative;
}

.checkbox-overlay {
  top: 4px;
  left: 4px;
  z-index: 2;
}

.document-thumbnail {
  width: 100px;
  height: 100px;
  border-radius: 4px;
  background-color: #c1c1c1;
  background-size: contain;
  background-position: center;
  background-repeat: no-repeat;
  margin: 0 auto;
  position: relative;
}

.document-thumbnail.loading {
  background-color: #c1c1c1;
}

.document-name {
  color: #333333;
  max-width: 100px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.right-panel {
  background-color: white;
  padding: 16px 20px;
  height: 100%;
}

.preview-container {
  width: 100%;
  text-align: center;
  height: 100%;
}

.pdf-viewer-container {
  text-align: center;
}

.image-preview img {
  border-radius: 8px;
}

.dialog-footer {
  background-color: white;
  border-top: 0.5px solid #c1c1c1;
  min-height: 48px;
}

.text-success {
  color: #14ae5c;
}

.text-color-hint {
  color: #848484;
}

/* Single Selection Preview Styles */
.single-selection {
  width: 100%;
  text-align: left;
  padding: 16px;
}

.preview-image {
  text-align: center;
}

.action-buttons .yz-btn {
  min-width: 58px;
}

.file-details {
  max-width: 200px;
}

.detail-item {
  line-height: 1.4;
}

.uploaded-by .avatar {
  flex-shrink: 0;
}

/* Multiple Selection Preview Styles */
.multiple-selection {
  width: 100%;
  text-align: left;
}

.selection-header {
  position: sticky;
  top: 0;
  background-color: white;
  z-index: 10;
  border-bottom: 1px solid #e5e5e5;
  padding-bottom: 12px;
}

.selected-documents-list {
  max-height: 595px;
  overflow-y: auto;
}

.selected-document-item {
  width: 100%;
  height: 70px;
  padding: 8px 0;
}

.selected-document-item:last-child {
  border-bottom: none;
}

.document-thumbnail-small {
  width: 70px;
  height: 70px;
  flex-shrink: 0;
}

.thumbnail-container {
  width: 100%;
  height: 100%;
  border-radius: 4px;
  background-color: #f5f5f5;
  background-size: cover;
  background-position: center;
  background-repeat: no-repeat;
  border: 1px solid #e5e5e5;
}

.document-name {
  padding: 0 8px;
}

.remove-button {
  flex-shrink: 0;
}

/* Custom scrollbar for selected documents list */
.selected-documents-list::-webkit-scrollbar {
  width: 4px;
}

.selected-documents-list::-webkit-scrollbar-track {
  background: #eaecf0;
  border-radius: 8px;
}

.selected-documents-list::-webkit-scrollbar-thumb {
  background: #c1c1c1;
  border-radius: 8px;
}

/* Custom scrollbar for documents grid */
.documents-grid::-webkit-scrollbar {
  width: 4px;
}

.documents-grid::-webkit-scrollbar-track {
  background: #eaecf0;
  border-radius: 8px;
}

.documents-grid::-webkit-scrollbar-thumb {
  background: #c1c1c1;
  border-radius: 8px;
}

/* Table View Styles */
.documents-table {
  max-height: 507px;
  overflow-y: auto;
}

/* Alternating row colors */
::v-deep .documents-table tbody tr:nth-child(even) {
  background-color: #f9fafb !important;
}

/* Selected row highlight */
::v-deep .documents-table tbody tr.v-data-table__selected {
  background-color: #e3f2fd !important;
}

/* Row cursor and hover */
::v-deep .documents-table tbody tr {
  cursor: pointer;
}

::v-deep .documents-table tbody tr:hover {
  background-color: var(--yz-neutral-100) !important;
}

/* Custom scrollbar for table */
.documents-table::-webkit-scrollbar {
  width: 4px;
}

.documents-table::-webkit-scrollbar-track {
  background: #eaecf0;
  border-radius: 8px;
}

.documents-table::-webkit-scrollbar-thumb {
  background: #c1c1c1;
  border-radius: 8px;
}
</style>
